import { useQuery } from '@tanstack/react-query';
import compareDesc from 'date-fns/compareDesc';
import { produce } from 'immer';
import _ from 'lodash-es';
import { JSX } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { GlobalsContext } from 'Shared/context';

import { OutlineButton } from '@/components/Buttons';
import Loader from '@/components/Loader';
import { useReadAllNotifications } from '@/hooks/notifications';
import { getAnnouncementsQueryOptions } from '@/queries/announcements';
import { useSelector } from '@/redux/helper';

import Announcements from '../Announcements';
import EmptyNotifications from './EmptyNotifiications';
import Notification from './Notification';
import { BellNotificationType } from './types';

interface NotificationItemProps {
  _id: string;
  title: string;
  body: string;
  createdAt: string;
  previewImage: string;
  profileImage: string;
  profile?: {
    animalIcon: string;
    color: string;
  };
  isViewed: boolean;
  type: BellNotificationType;
  actionURL: string;
}

interface NotificationSocketDataProps {
  data: NotificationItemProps[];
  total: number;
}

const INITIAL_DATA = [] as NotificationItemProps[];

const Notifications = () => {
  const userId = useSelector((store) => store.user.userId);
  const sessionChecked = useSelector((state) => state.user.sessionChecked);

  const [totalCount, setTotalCount] = useState(0);
  const { socket, userSocket } = useContext(GlobalsContext);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [data, setData] = useState(INITIAL_DATA);

  const { data: announcements } = useQuery({
    ...getAnnouncementsQueryOptions(),
    enabled: sessionChecked && !!userId,
  });
  const { mutate: markAsRead, isPending: loading, error } = useReadAllNotifications();

  const hasNotifications = (announcements?.length ?? 0) > 0 || data.length > 0;

  // Subscribe notifications
  useEffect(() => {
    let unmounted = false;
    const socketHandler = ({ data: newData, total }: NotificationSocketDataProps) => {
      if (unmounted) {
        return;
      }
      setIsInitialLoaded(true);
      setTotalCount(total);

      setData(
        produce((draft) => {
          newData.forEach((newNotification) => {
            const index = draft.findIndex((item) => item._id === newNotification._id);
            if (index !== -1) {
              // eslint-disable-next-line no-param-reassign
              draft[index] = { ...draft[index], ...newNotification };
            } else {
              draft.push(newNotification);
            }
          });

          draft.sort((a, b) => compareDesc(new Date(a.createdAt), new Date(b.createdAt)));
        }),
      );
    };

    if (!socket || !userSocket || !userId) {
      return undefined;
    }

    let id: ReturnType<typeof setTimeout> | undefined;

    userSocket.on('receiveNotifications', socketHandler);

    const trigger = (trialsLeft = 5) => {
      if (unmounted || trialsLeft <= 0) {
        return;
      }

      if (!userSocket.connected) {
        id = setTimeout(() => trigger(trialsLeft - 1), 1000);
        return;
      }

      setIsInitialLoaded(false);
      socket.emit('getNotifications', { userId });
    };

    trigger();

    return () => {
      unmounted = true;
      if (id) clearTimeout(id);
      userSocket.off('receiveNotifications', socketHandler);
    };
  }, [socket, userSocket, userId]);

  const notificationClickHandler = (notificationId: string) => {
    socket?.emit('viewNotification', { userId, notificationId });
  };

  const onScrollHandler: JSX.UIEventHandler<HTMLElement> = ({ currentTarget }) => {
    if (
      currentTarget.scrollHeight - (currentTarget.scrollTop + currentTarget.clientHeight) < 10 &&
      data.length < totalCount &&
      socket
    ) {
      socket.emit('getNotifications', { userId, notificationId: _.last(data)?._id });
    }
  };

  // log error
  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  return (
    <section className="flex h-full w-full flex-col bg-white">
      <div className="flex items-center justify-between p-4">
        <h4 className="preflight preflight-h4 font-semibold">All Notifications</h4>
        {hasNotifications && (
          <OutlineButton loading={loading} size="medium" label="Mark as read" onClick={() => markAsRead()} />
        )}
      </div>
      <div className="customScrollBar grey min-h-0 grow" onScroll={onScrollHandler}>
        {!isInitialLoaded ? (
          <Loader fill />
        ) : !hasNotifications ? (
          <EmptyNotifications />
        ) : (
          <>
            <Announcements data={announcements} />

            <ul className="preflight preflight-ul px-2 pb-6">
              {data.map((item) => (
                <li key={item._id}>
                  <Notification
                    id={item._id}
                    title={item.title}
                    description={item.body}
                    time={item.createdAt}
                    media={item.previewImage}
                    viewed={item.isViewed}
                    actionURL={item.actionURL}
                    type={item.type}
                    onClick={notificationClickHandler}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
};

export default Notifications;
